import React, { useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import { find, get } from 'lodash';

import { AppSkeleton } from '@/component';
import { Button, Dropdown, Input, DatePickerInput } from '@/kaya';
import { userActions } from '@/actions';
import { useArtistStore } from '@/store';
import { authTypes } from '@/types';
import { authHelpers, generalHelpers } from '@/helpers';
import { generalConstants, iconConstants, routesConstants } from '@/constants';

import styles from './Profile.module.scss';
import Icons from '@/icons';
import { BASE_URL, MEDIA_URL } from '@/axios';

const Profile = () => {
  const navigate = useNavigate();
  const { setArtistToDefault, artist, setArtist } = useArtistStore();

  const [loading, setLoading] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [toggle, setToggle] = React.useState(true);
  const photoRef = React.useRef<HTMLInputElement>(null);
  const [formValues, setFormValues] = React.useState<authTypes.Profile>(
    authTypes.INITIAL_PROFILE_VALUES,
  );
  const [err, setErr] = React.useState({
    name: '',
    national_identity_no: '',
    birthday: '',
    country: '',
  });

  const handleSave = async () => {
    const error = {
      name: authHelpers.validateName(get(formValues, 'name')),
      national_identity_no: authHelpers.validateNationalNo(
        get(formValues, 'national_identity_no', '').toString(),
      ),

      birthday: authHelpers.validateDate(get(formValues, 'birthday')),
      country: authHelpers.validateCountry(get(formValues, 'country.value', '')),
    };

    if (!generalHelpers.isEmptyErrors(error)) {
      setErr(error);
      return;
    }

    setIsLoading(true);

    const promises = [];
    const updateUserPromise = userActions.updateArtistAction({
      payload: {
        first_name: get(formValues, 'name').split(' ')[0],
        last_name: get(formValues, 'name').split(' ').slice(1).join(' '),
        national_identity_no: Number(get(formValues, 'national_identity_no')),
        stage_name: get(formValues, 'stage_name'),
        birthday: get(formValues, 'birthday'),
        country: get(formValues, 'country.value'),
      },
      id: artist.id,
    });
    promises.push(updateUserPromise);
    if (formValues.profile_img && typeof formValues.profile_img !== 'string') {
      const uploadAvtarPromise = userActions.uploadAvtarAction(formValues.profile_img, artist.id);
      promises.push(uploadAvtarPromise);
    }

    const results = await Promise.allSettled(promises);

    let allSuccess = true;
    for (const result of results) {
      if (result.status === 'rejected') {
        allSuccess = false;
        break;
      }
      if (!result.value.success) {
        allSuccess = false;
        break;
      }
    }

    if (allSuccess) {
      const fetchRes = await userActions.fetchArtistAction(artist.id);
      if (fetchRes.success) {
        setArtist(fetchRes.data);
        setFormValues((prev) => ({
          ...prev,
          profile_img_url: fetchRes.data.profile_img ? `${BASE_URL}/media/images/${fetchRes.data.profile_img}` : '',
          profile_img: undefined,
        }));
        generalHelpers.toaster('success', 'Profile updated successfully');
      }
    } else {
      generalHelpers.toaster('error', 'Failed to update profile');
    }

    setIsLoading(false);
    setToggle(!toggle);
  };

  const handleChange =
    (id: string | object) => (value?: string | object | boolean | File | Date) => {
      if (typeof id !== 'object') {
        setFormValues({
          ...formValues,
          [id]: value,
        });
        setErr({
          ...err,
          [id]: '',
        });
      } else {
        setFormValues({
          ...formValues,
          ...id,
        });
        setErr({
          ...err,
          ...id,
        });
      }
    };

  const handleLogout = async () => {
    setLoading(true);
    const data = await userActions.logoutAction();
    if (data.success) {
      setArtistToDefault();
      //* remove token from local storage
      localStorage.removeItem('refresh_token');
      navigate(routesConstants.ROUTES.LOGIN);
    }

    setLoading(false);
  };

  const handleRemoveImg = async () => {
    setFormValues({
      ...formValues,
      profile_img: '',
      profile_img_url: '',
    });
    const deleteRes = await userActions.deleteAvtarAction(artist.id);
    if (deleteRes.success) {
      const fetchRes = await userActions.fetchArtistAction(artist.id);
      if (fetchRes.success) {
        setArtist(fetchRes.data);
      }
      generalHelpers.toaster('success', 'Avatar removed successfully');
    } else {
      generalHelpers.toaster('error', 'Failed to remove avatar');
    }
  };

  const disableButton = () => {
    if (
      formValues.name === `${get(artist, 'first_name', '')} ${get(artist, 'last_name', '')}` &&
      formValues.birthday?.getTime() === new Date(get(artist, 'birthday')).getTime() &&
      formValues.country === find(generalConstants.countries, { value: get(artist, 'country') }) &&
      formValues.national_identity_no === get(artist, 'national_identity_no') &&
      formValues.stage_name === get(artist, 'stage_name') &&
      formValues.profile_img_url === get(artist, 'profile_img')
    ) {
      return true;
    }
    return false;
  };

  const handleImg = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (get(file, 'size', 0) > 4000000) {
      generalHelpers.toaster('error', 'File size must be less than 4mb');
      return;
    }
    handleChange({ profile_img: file, profile_img_url: URL.createObjectURL(file || ({} as File)) })(
      '',
    );

    // const formData = new FormData();
    // formData.append('avatar', file as File);
    // userActions.uploadAvtarAction(formData, artist.id);
  };

  useEffect(() => {
    setFormValues({
      ...formValues,

      //* use get lodash to get the value of the object
      name: `${get(artist, 'first_name', '')} ${get(artist, 'last_name', '')}`,
      birthday: new Date(get(artist, 'birthday')),
      country:
        find(generalConstants.countries, { value: get(artist, 'country') }) ||
        generalConstants.countries[0],
      national_identity_no: get(artist, 'national_identity_no'),
      stage_name: get(artist, 'stage_name'),
      profile_img_url: get(artist, 'profile_img')
        ? `${MEDIA_URL}/images/${get(artist, 'profile_img')}`
        : '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artist]);

  return (
    <AppSkeleton>
      <div className={styles.Profile}>
        <div className={styles.header}>
          <h1 className={styles.heading}>Profile</h1>
          <Button className={styles.btn} type="Outline" onClick={handleLogout} isLoading={loading}>
            Log Out
          </Button>
        </div>

        <div className={styles.main}>
          <div className={styles.box}>
            <div className={styles.labelWithinput}>
              <p className={styles.label}>Username</p>

              <Input
                placeholder="Username"
                containerClassName={styles.input}
                value={get(artist, 'username', '')}
                disabled
              />
            </div>
            <div className={styles.labelWithinput}>
              <p className={styles.label}>Name</p>

              <Input
                placeholder="Name"
                containerClassName={styles.input}
                value={get(formValues, 'name', '')}
                onChange={handleChange('name')}
                id="name"
                errors={err}
              />
            </div>
            <div className={styles.labelWithinput}>
              <p className={styles.label}>Email</p>

              <Input
                placeholder="Email"
                containerClassName={styles.input}
                value={get(artist, 'email', '')}
                disabled
              />
            </div>
            <div className={styles.labelWithinput}>
              <p className={styles.label}>Phone No</p>

              <Input
                placeholder="Phone No."
                containerClassName={styles.input}
                value={get(artist, 'phone_no', '').toString()}
                disabled
              />
            </div>
            <div className={styles.labelWithinput}>
              <p className={styles.label}>D.O.B</p>
              {/* 
            <Input
              placeholder="D.O.B"
              containerClassName={styles.input}
              ={toggle}
              value={formValues.birthday}
              onChange={handleChange('birthday')}
              id="birthday"
              errors={err}
            /> */}

              <DatePickerInput
                placeholder="Birthdate"
                setDate={handleChange('birthday')}
                date={formValues.birthday}
                width="480px"
                error={err.birthday}
                id="birthday"
              />
            </div>
            <div className={styles.labelWithinput}>
              <p className={styles.label}>Country</p>
              <Dropdown
                className={styles.input}
                placeholder="Country"
                options={generalConstants.countries}
                value={get(formValues, 'country', '')}
                errors={err}
                id="country"
                onMenuItemClick={handleChange('country')}
              />
            </div>

            <div className={styles.labelWithinput}>
              <p className={styles.label}>National I’d No</p>

              <Input
                placeholder="National I’d No."
                containerClassName={styles.input}
                value={get(formValues, 'national_identity_no', '').toString()}
                onChange={handleChange('national_identity_no')}
                id="national_identity_no"
                errors={err}
                maxLength={9}
              />
            </div>
            <div className={styles.labelWithinput}>
              <p className={styles.label}>Stage Name</p>

              <Input
                placeholder="Stage Name"
                containerClassName={styles.input}
                value={get(formValues, 'stage_name', '')}
                onChange={handleChange('stage_name')}
                id="stage_name"
              />
            </div>
            {/* {toggle ? (
            <Button
              className={styles.btn}
              type="Primary"
              onClick={handleEdit}
              isLoading={isLoading}
            >
              Edit
            </Button>
          ) : ( */}
            <Button
              className={styles.btn}
              type="Primary"
              onClick={handleSave}
              isLoading={isLoading}
              disabled={disableButton()}
            >
              Save
            </Button>
            {/* )} */}
          </div>

          <div className={styles.rightSide}>
            {!formValues?.profile_img_url ? (
              <div className={styles.imgContainer} onClick={() => photoRef.current?.click()}>
                <Icons type={iconConstants.ICONS.MEDIA} />
                <p className={styles.upload}>Upload Photo</p>
              </div>
            ) : (
              <div className={styles.imgContainer}>
                <img
                  src={formValues?.profile_img_url}
                  alt="Picture of the author"
                  width={180}
                  height={160}
                  className={styles.image}
                />
                <div className={styles.closeIcon} onClick={handleRemoveImg}>
                  <Icons type={iconConstants.ICONS.CLOSE2} />
                </div>
              </div>
            )}

            <p className={styles.imgTag}>(png, jpg, jpeg only)</p>

            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              ref={photoRef}
              onChange={handleImg}
            />
          </div>
        </div>
      </div>
    </AppSkeleton>
  );
};

export default Profile;
